import GlobalButton from 'Components/common/components/GlobalButton';
import TableView from 'Components/common/components/TableView';
import styled from 'styled-components';
import { ComplianceDashboard } from 'Types/SRITypes';
import { ColumnType, TableColValue } from 'Types/TableTypes';
import { useTable } from 'Utils/hooks/useTable';
import {
  boxShadow, primaryHeadingColor, scrollbar, secondaryFont,
} from 'Utils/utilsStyle';
import ExportIcon from 'Components/common/assets/svgIcons/ExportIcon';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from 'Slice/SRISlice';
import { selectComplianceExportLoading } from 'Selector/SRISelector';
import { getTimezoneInGMT } from 'Utils/UtilityFunctions';
import { selectUserCompanyId } from 'Selector/GlobalSearchContentSelector';
import ChartSpinner from './ChartSpinner';

const Wrapper = styled.div`
  border-radius: 16px;
  box-shadow: ${boxShadow};
  background-color: #fff;
  margin-bottom: 40px;
  padding-right: 4px;
  padding-bottom: 5px;
  .header {
    display: flex;
    justify-content: space-between;
    padding: 24px 24px 24px 42px;
    .title {
      font-family: ${secondaryFont};
      font-size: 28px;
      font-weight: 700;
      line-height: 47px;
      color: ${primaryHeadingColor};
    }
    button {
      height: 38px;
      font-size: 16px;
      line-height: 20px;
      svg {
        height: 16px;
        width: 14px;
        margin-right: 4px;
      }
    }
  }
  .table {
    .table-header {
      padding: 0px 42px;
      .table-cell {
        &:nth-child(1) {
          .cell-name {
            padding-left: 40px;
          }
        }
        &:nth-child(3) {
          width: 100%;
          text-align: center;
        }
        &:nth-child(4) {
          width: 100%;
          text-align: center;
        }
      }
    }
    .table-body {
      .infinite-scroll-component {
        padding: 0px 30px 8px 30px;
        ${scrollbar}
        .table-row {
          .table-cell {
            &:nth-child(3) {
              width: 100%;
              text-align: center;
            }
            &:nth-child(4) {
              width: 100%;
              text-align: center;
            }
          }
        }
      }
    }
  }
`;

const columnValues: TableColValue[] = [
  {
    id: 'person',
    label: 'Name',
    type: ColumnType.PERSON,
  },
  {
    id: 'ackDateTime',
    label: 'Date/time',
    type: ColumnType.DATE,
  },
  {
    id: 'version',
    label: 'Privacy Policy Version',
    type: ColumnType.DEFAULT,
  },
  {
    id: 'receipt',
    label: 'Compliance Receipt',
    type: ColumnType.GREEN_TEXT,
  },
];

type Props = {
  complianceData: ComplianceDashboard[];
  complianceDataLoading: boolean;
};

const ComplianceTable = function ComplianceTable(props: Props) {
  const { complianceData, complianceDataLoading } = props;
  const dispatch = useDispatch();
  const columns = useTable<ComplianceDashboard>(columnValues);
  const complianceExportLoading = useSelector(selectComplianceExportLoading);
  const userCompanyId = useSelector(selectUserCompanyId);
  const timezone = getTimezoneInGMT();

  const handleExport = () => {
    dispatch(actions.exportComplianceData({ companyId: userCompanyId, timezone }));
  };

  const fetchMore = () => {
    dispatch(actions.getComplianceData({
      offset: complianceData?.length,
      limit: 20,
      timezone,
    }));
  };

  return (
    <Wrapper>
      {complianceDataLoading && !complianceData?.length
        ? <ChartSpinner />
        : (
          <>
            <div className="header">
              <div className="title">Compliance Dashboard</div>
              <GlobalButton
                title="Export"
                textColor="#7E7E7E"
                bgColor="#fff"
                outlineColor="#DADADA"
                innerSpace="5px 15px 5px 15px"
                PaddingSm="6px 15px"
                LeftSpace="7px"
                IconLeft={<ExportIcon />}
                buttonClassName="share-button mr-24 export_btn"
                rightSeparatorClassName="right-separator-class"
                onButtonClick={handleExport}
                disabled={complianceExportLoading}
              />
            </div>
            <TableView
              columns={columns}
              loading={complianceDataLoading}
              records={complianceData}
              height={272}
              fetchMore={fetchMore}
            />
          </>
        )}
    </Wrapper>
  );
};

export default ComplianceTable;
